import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Settings, Globe, DollarSign, Headphones, Link, AlertTriangle,
  Save, Image, Type, Percent, ShoppingCart, Mail, Phone,
  FileText, Shield, Power,
} from 'lucide-react'
import { settingsService } from '@/services/settingsService'
import { imgUrl } from '@/utils/img'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import MediaPickerModal from '@/components/shared/MediaPickerModal'

const LBL = {
  display: 'block',
  fontFamily: 'Cairo, sans-serif',
  fontWeight: 700,
  fontSize: 12,
  color: '#454652',
  marginBottom: 6,
}

const INP = {
  width: '100%',
  borderRadius: 10,
  background: '#E2E2E2',
  border: 'none',
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'Tajawal, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#1A1C1C',
}

function SectionCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 20,
      padding: 24,
      boxShadow: '0 2px 4px rgba(26,28,28,0.04), 0 8px 16px rgba(26,28,28,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: 'rgba(0,6,102,0.08)', color: '#000666',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={18} />
        </div>
        <div>
          <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 15, color: '#1A1C1C' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 1 }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

function FieldRow({ children, cols = 2 }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 12,
    }}>
      {children}
    </div>
  )
}

export default function SettingsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data: settings, isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: settingsService.getSettings,
  })

  const [mediaPicker, setMediaPicker] = useState({ open: false, field: null })

  const [form, setForm] = useState({
    platformNameAr: '',
    platformNameEn: '',
    logo: '',
    favicon: '',
    currency: 'SAR',
    taxRate: '',
    maxCartItems: '',
    supportEmail: '',
    supportPhone: '',
    termsUrl: '',
    privacyUrl: '',
    maintenanceMode: false,
  })

  // Sync from API
  useEffect(() => {
    if (!settings) return
    setForm({
      platformNameAr: settings.platformName?.ar || '',
      platformNameEn: settings.platformName?.en || '',
      logo: settings.logo || '',
      favicon: settings.favicon || '',
      currency: settings.currency || 'SAR',
      taxRate: settings.taxRate != null ? String(settings.taxRate) : '',
      maxCartItems: settings.maxCartItems != null ? String(settings.maxCartItems) : '',
      supportEmail: settings.supportEmail || '',
      supportPhone: settings.supportPhone || '',
      termsUrl: settings.termsUrl || '',
      privacyUrl: settings.privacyUrl || '',
      maintenanceMode: settings.maintenanceMode || false,
    })
  }, [settings])

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }))

  const updateMut = useMutation({
    mutationFn: (payload) => settingsService.updateSettings(payload),
    onSuccess: () => {
      toast.success(t('settings.saveSuccess'))
      qc.invalidateQueries({ queryKey: ['platform-settings'] })
    },
    onError: (e) => toast.error(e?.response?.data?.message || t('settings.saveError')),
  })

  const handleSave = () => {
    const payload = {}

    // Build platformName only if at least one is set
    if (form.platformNameAr || form.platformNameEn) {
      payload.platformName = {}
      if (form.platformNameAr) payload.platformName.ar = form.platformNameAr
      if (form.platformNameEn) payload.platformName.en = form.platformNameEn
    }

    if (form.logo)         payload.logo         = form.logo
    if (form.favicon)      payload.favicon       = form.favicon
    if (form.currency)     payload.currency      = form.currency
    if (form.supportEmail) payload.supportEmail  = form.supportEmail
    if (form.supportPhone) payload.supportPhone  = form.supportPhone
    if (form.termsUrl)     payload.termsUrl      = form.termsUrl
    if (form.privacyUrl)   payload.privacyUrl    = form.privacyUrl

    if (form.taxRate !== '') payload.taxRate = parseFloat(form.taxRate) || 0
    if (form.maxCartItems !== '') payload.maxCartItems = parseInt(form.maxCartItems) || 1

    payload.maintenanceMode = form.maintenanceMode

    updateMut.mutate(payload)
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: '#000666', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 20px rgba(0,6,102,0.3)',
        }}>
          <Settings size={20} />
        </div>
        <div>
          <h1 style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 20, color: '#1A1C1C', margin: 0 }}>
            {t('settings.pageTitle')}
          </h1>
          <p style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#767683', margin: 0 }}>
            {t('settings.pageSubtitle')}
          </p>
        </div>
      </div>

      {/* ── Section 1: Platform Identity ───────────────────────────────────── */}
      <SectionCard icon={Globe} title={t('settings.identityTitle')} subtitle={t('settings.identitySubtitle')}>
        <FieldRow>
          <div>
            <label style={LBL}>{t('settings.platformNameArLabel')}</label>
            <input
              value={form.platformNameAr}
              onChange={(e) => set('platformNameAr', e.target.value)}
              placeholder={t('settings.platformNameArPlaceholder')}
              style={INP}
            />
          </div>
          <div>
            <label style={LBL}>{t('settings.platformNameEnLabel')}</label>
            <input
              value={form.platformNameEn}
              onChange={(e) => set('platformNameEn', e.target.value)}
              placeholder="e.g. Hagatna"
              dir="ltr"
              style={INP}
            />
          </div>
        </FieldRow>
        <FieldRow cols={2} >
          {[
            { field: 'logo',    label: t('settings.logoLabel'),  placeholder: 'https://cdn.hagatna.com/logo.png',    previewH: 32 },
            { field: 'favicon', label: t('settings.faviconLabel'),    placeholder: 'https://cdn.hagatna.com/favicon.ico', previewH: 24 },
          ].map(({ field, label, placeholder, previewH }) => (
            <div key={field} style={{ marginTop: 12 }}>
              <label style={LBL}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                  <Image size={12} /> {label}
                </span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={form[field]}
                  onChange={(e) => set(field, e.target.value)}
                  placeholder={placeholder}
                  dir="ltr"
                  style={{ ...INP, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => setMediaPicker({ open: true, field })}
                  title={t('settings.selectFromLibrary')}
                  style={{
                    flexShrink: 0, padding: '0 14px', borderRadius: 10,
                    background: 'rgba(0,6,102,0.08)', color: '#000666',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                    fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 12,
                  }}
                >
                  <Image size={13} />{t('settings.libraryBtn')}
                </button>
              </div>
              {form[field] && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <img
                    src={form[field]} alt="preview"
                    style={{ height: previewH, borderRadius: 6, objectFit: 'contain', background: '#F3F3F3', padding: 4 }}
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <span style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 11, color: '#767683' }}>{t('settings.previewLabel')}</span>
                </div>
              )}
            </div>
          ))}
        </FieldRow>
      </SectionCard>

      {/* ── Section 2: Currency & Commerce ─────────────────────────────────── */}
      <SectionCard icon={DollarSign} title={t('settings.currencyTitle')} subtitle={t('settings.currencySubtitle')}>
        <FieldRow cols={3}>
          <div>
            <label style={LBL}>{t('settings.defaultCurrencyLabel')}</label>
            <select
              value={form.currency}
              onChange={(e) => set('currency', e.target.value)}
              style={{ ...INP, cursor: 'pointer' }}
            >
              {['SAR','AED','EGP','USD','EUR','KWD','QAR','BHD','OMR','JOD'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Percent size={11} /> {t('settings.taxRateLabel')}
              </span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.taxRate}
              onChange={(e) => set('taxRate', e.target.value)}
              placeholder="0"
              dir="ltr"
              style={INP}
            />
          </div>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShoppingCart size={11} /> {t('settings.maxCartItemsLabel')}
              </span>
            </label>
            <input
              type="number"
              min="1"
              value={form.maxCartItems}
              onChange={(e) => set('maxCartItems', e.target.value)}
              placeholder="20"
              dir="ltr"
              style={INP}
            />
          </div>
        </FieldRow>
      </SectionCard>

      {/* ── Section 3: Support Contact ──────────────────────────────────────── */}
      <SectionCard icon={Headphones} title={t('settings.supportTitle')} subtitle={t('settings.supportSubtitle')}>
        <FieldRow>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Mail size={11} /> {t('settings.supportEmailLabel')}
              </span>
            </label>
            <input
              type="email"
              value={form.supportEmail}
              onChange={(e) => set('supportEmail', e.target.value)}
              placeholder="support@hagatna.com"
              dir="ltr"
              style={INP}
            />
          </div>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Phone size={11} /> {t('settings.supportPhoneLabel')}
              </span>
            </label>
            <input
              type="tel"
              value={form.supportPhone}
              onChange={(e) => set('supportPhone', e.target.value)}
              placeholder="+966500000000"
              dir="ltr"
              style={INP}
            />
          </div>
        </FieldRow>
      </SectionCard>

      {/* ── Section 4: Legal Links ──────────────────────────────────────────── */}
      <SectionCard icon={FileText} title={t('settings.legalTitle')} subtitle={t('settings.legalSubtitle')}>
        <FieldRow>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Link size={11} /> {t('settings.termsUrlLabel')}
              </span>
            </label>
            <input
              value={form.termsUrl}
              onChange={(e) => set('termsUrl', e.target.value)}
              placeholder="https://hagatna.com/terms"
              dir="ltr"
              style={INP}
            />
          </div>
          <div>
            <label style={LBL}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Shield size={11} /> {t('settings.privacyUrlLabel')}
              </span>
            </label>
            <input
              value={form.privacyUrl}
              onChange={(e) => set('privacyUrl', e.target.value)}
              placeholder="https://hagatna.com/privacy"
              dir="ltr"
              style={INP}
            />
          </div>
        </FieldRow>
      </SectionCard>

      {/* ── Section 5: Maintenance Mode ────────────────────────────────────── */}
      <SectionCard icon={Power} title={t('settings.maintenanceTitle')} subtitle={t('settings.maintenanceSubtitle')}>

        {/* Warning card — shown when ON */}
        {form.maintenanceMode && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            background: 'rgba(186,26,26,0.06)',
            border: '1px solid rgba(186,26,26,0.2)',
            borderRadius: 14, padding: '14px 16px', marginBottom: 16,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'rgba(186,26,26,0.12)', color: '#BA1A1A',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#BA1A1A', marginBottom: 3 }}>
                {t('settings.maintenanceWarningTitle')}
              </div>
              <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 13, color: '#454652', lineHeight: 1.6 }}>
                {t('settings.maintenanceWarningBody')}
              </div>
            </div>
          </div>
        )}

        {/* Toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px',
          background: form.maintenanceMode ? 'rgba(186,26,26,0.04)' : '#F8F8FB',
          borderRadius: 14,
          border: `1px solid ${form.maintenanceMode ? 'rgba(186,26,26,0.15)' : '#EBEBEB'}`,
          transition: 'all 0.2s',
        }}>
          <div>
            <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 14, color: '#1A1C1C' }}>
              {t('settings.maintenanceToggleLabel')}
            </div>
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 2 }}>
              {form.maintenanceMode ? t('settings.maintenanceOffline') : t('settings.maintenanceOnline')}
            </div>
          </div>
          {/* Custom Toggle Switch */}
          <button
            onClick={() => set('maintenanceMode', !form.maintenanceMode)}
            style={{
              width: 52, height: 28, borderRadius: 14,
              background: form.maintenanceMode ? '#BA1A1A' : '#C4C4C4',
              border: 'none', cursor: 'pointer', position: 'relative',
              transition: 'background 0.25s', flexShrink: 0,
              boxShadow: form.maintenanceMode ? '0 4px 12px rgba(186,26,26,0.3)' : 'none',
            }}
            aria-label="toggle maintenance mode"
          >
            <span style={{
              position: 'absolute',
              top: 3,
              right: form.maintenanceMode ? 3 : undefined,
              left: form.maintenanceMode ? undefined : 3,
              width: 22, height: 22, borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.25s',
              display: 'block',
            }} />
          </button>
        </div>
      </SectionCard>

      {/* ── Media Picker ────────────────────────────────────────────────────── */}
      <MediaPickerModal
        open={mediaPicker.open}
        onClose={() => setMediaPicker({ open: false, field: null })}
        onSelect={({ url }) => { set(mediaPicker.field, imgUrl(url)); setMediaPicker({ open: false, field: null }) }}
        title={t('settings.selectImageTitle')}
      />

      {/* ── Save Button ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
        <button
          onClick={handleSave}
          disabled={updateMut.isPending}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 32px', borderRadius: 14,
            fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 15,
            color: '#fff', background: '#000666', border: 'none',
            cursor: updateMut.isPending ? 'not-allowed' : 'pointer',
            opacity: updateMut.isPending ? 0.6 : 1,
            boxShadow: '0 8px 24px rgba(0,6,102,0.25)',
            transition: 'all 0.2s',
          }}
        >
          <Save size={17} />
          {updateMut.isPending ? t('orders.saving') : t('settings.saveSettingsBtn')}
        </button>
      </div>

    </div>
  )
}
